import Button from '@/common/components/base/button/Button';
import LoadingButton from '@/common/components/base/button/loadingButton';
import ModalInput from '@/common/components/base/input/modalInput/ModalInput';
import TextInput from '@/common/components/base/input/textInput/TextInput';
import Typography from '@/common/components/base/typography/Typography';
import MainLayout from '@/common/components/layouts/MainLayout';
import useModal from '@/common/hooks/useModal';
import LoadingPage from '@/common/pages/LoadingPage';
import { PUBLIC_REQUIRED_RULE } from '@/common/utils/rules/publicRules';
import { ADJUSTER_REPORT_LINKS } from '@/modules/adjusterReportModules/adjusterReport/AdjusterReportLinks';
import { PoliceReportFormType } from '@/modules/adjusterReportModules/adjusterReport/pages/policeReport/policeReport.type';
import PoliceReportInquiryModal from '@/modules/adjusterReportModules/adjusterReport/pages/policeReport/PoliceReportInquiryModal';
import { getPoliceReportQueryOptions } from '@/modules/adjusterReportModules/adjusterReport/queries/policeReport/getPoliceReport/getPoliceReportQueryOptions';
import { getReportTypeQueryOptions } from '@/modules/adjusterReportModules/adjusterReport/queries/policeReport/getReportType/getReportTypeQueryOptions';
import { postPoliceReportQueryOptions } from '@/modules/adjusterReportModules/adjusterReport/queries/policeReport/postPoliceReport/postPoliceReportQueryOptions';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useWatch,
} from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Divider, Flex } from 'styled-system/jsx';
import ArrowRightIcon from '@/common/assets/icons/arrowRightIcon.svg?react';
import { css } from 'styled-system/css';
import { token } from 'styled-system/tokens';

export enum ReportTypes {
  compromise_sketch = 'compromise_sketch', // کروکی سازشی
  uncompromising_sketch = 'uncompromising_sketch', // کروکی غیر سازشی
  military_reference = 'military_reference', // گزارش مرجع انتظامی
  others = 'others', // سایر
  not_exist = 'not_exist', // ندارد
}
export default () => {
  return (
    <MainLayout title="گزارش پلیس" header="titleHeader" hasBottomPadding>
      <PoliceReport />
    </MainLayout>
  );
};
const PoliceReport = () => {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const { closeModal, open, openModal } = useModal();
  const methods = useForm<PoliceReportFormType>({ mode: 'onChange' });
  const navigate = useNavigate();
  const { isPending: reportTypeLoading, data: reportTypeData } = useQuery(
    getReportTypeQueryOptions(),
  );

  const { isPending: policeReportLoading } = useQuery(
    getPoliceReportQueryOptions({
      id: id!,
      onSuccess: (data) => {
        methods.reset({
          ...data,
          officerType: 'old',
          police_report_type: {
            label: data.police_report_type?.fa_const,
            value: data.police_report_type?.const,
          },
          police_croquis_serial_number: {
            label: data.police_croquis_serial_number,
            value: data.police_croquis_serial_number,
          },
        });
      },
    }),
  );

  const { mutateAsync, isPending: postLoading } = useMutation({
    ...postPoliceReportQueryOptions(),
    onSuccess: () =>
      navigate(ADJUSTER_REPORT_LINKS?.adjusterReport(id!), { replace: true }),
  });

  const reportType = useWatch({
    name: 'police_report_type',
    control: methods.control,
  });

  const fieldRules = useMemo(() => {
    return (reportType: PoliceReportFormType['police_report_type']) => {
      switch (reportType?.value) {
        case ReportTypes.compromise_sketch:
          return {
            rules: {
              police_croquis_serial_number: PUBLIC_REQUIRED_RULE,
              police_croquis_serie_number: PUBLIC_REQUIRED_RULE,
              police_name: PUBLIC_REQUIRED_RULE,
              police_report_code: PUBLIC_REQUIRED_RULE,
              police_report_body: false,
            },
          };
        case ReportTypes.uncompromising_sketch:
          return {
            disabled: {
              police_croquis_serial_number: false,
              police_croquis_serie_number: false,
            },
            rules: {
              police_croquis_serial_number: PUBLIC_REQUIRED_RULE,
              police_croquis_serie_number: PUBLIC_REQUIRED_RULE,
              police_name: PUBLIC_REQUIRED_RULE,
              police_report_code: PUBLIC_REQUIRED_RULE,
              police_report_body: false,
            },
          };
        case ReportTypes.not_exist:
          return {
            disabled: {
              police_croquis_serial_number: true,
              police_croquis_serie_number: true,
            },
            rules: {
              police_croquis_serial_number: false,
              police_croquis_serie_number: false,
              police_name: false,
              police_report_code: false,
              police_report_body: false,
            },
          };

        case ReportTypes.others:
        case ReportTypes.military_reference:
          return {
            disabled: {
              police_croquis_serial_number: true,
              police_croquis_serie_number: true,
            },
            rules: {
              police_croquis_serial_number: false,
              police_croquis_serie_number: false,
              police_name: false,
              police_report_code: false,
              police_report_body: PUBLIC_REQUIRED_RULE,
            },
          };

        default:
          return {
            disabled: {
              police_croquis_serial_number: false,
              police_croquis_serie_number: false,
            },
            rules: {
              police_croquis_serial_number: PUBLIC_REQUIRED_RULE,
              police_croquis_serie_number: PUBLIC_REQUIRED_RULE,
              police_name: PUBLIC_REQUIRED_RULE,
              police_report_code: PUBLIC_REQUIRED_RULE,
              police_report_body: false,
            },
          };
      }
    };
  }, [reportType]);

  const onSubmit: SubmitHandler<PoliceReportFormType> = (data) => {
    const postData = {
      ...data,
      police_name_inquiry: null,
      police_report_code_inquiry: null,
      police_report_type: reportType?.value,
      police_croquis_serial_number: data?.police_croquis_serial_number?.value,
    } as Record<string, string | null>;
    for (const key in postData) {
      if (postData[key] === '') {
        postData[key] = null;
      }
    }
    mutateAsync({ data: postData, id: id! }).then(() => {
      queryClient.invalidateQueries({
        queryKey: getPoliceReportQueryOptions({ id: id! }).queryKey,
      });
    });
  };

  const options = {
    compromise_sketch: [
      { label: '11', value: 11 },
      { label: '12', value: 12 },
      { label: '100', value: 100 },
    ],
    uncompromising_sketch: [
      { label: '22', value: 22 },
      { label: '200', value: 200 },
    ],
  };

  if (policeReportLoading) {
    return <LoadingPage />;
  }
  return (
    <Box pt="2rem">
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <Flex mt="1rem" gap="1rem" flexDirection="column">
            <ModalInput
              name="police_report_type"
              label="نوع گزارش"
              placeholder="انتخاب کنید"
              loading={reportTypeLoading}
              options={reportTypeData || []}
              rules={{ required: PUBLIC_REQUIRED_RULE }}
              onChange={() => {
                if (
                  reportType?.value !== ReportTypes.compromise_sketch &&
                  reportType?.value !== ReportTypes.uncompromising_sketch
                ) {
                  methods.setValue('police_croquis_serial_number', {
                    label: '',
                    value: null,
                  });
                  methods.setValue('police_croquis_serie_number', '');
                }
                methods.clearErrors();
              }}
            />
            <ModalInput
              name="police_croquis_serial_number"
              label="شماره سریال کروکی"
              placeholder="شماره سریال کروکی را وارد کنید"
              disabled={
                fieldRules(reportType)?.disabled?.police_croquis_serial_number
              }
              optionalLabel={
                !fieldRules(reportType)?.rules?.police_croquis_serial_number
              }
              options={
                options?.[reportType?.value as keyof typeof options] || []
              }
              rules={{
                required:
                  fieldRules(reportType)?.rules?.police_croquis_serial_number,
              }}
            />
            <TextInput
              name="police_croquis_serie_number"
              label="شماره سری کروکی"
              placeholder="شماره سری کروکی را وارد کنید"
              max={7}
              rules={{
                required:
                  fieldRules(reportType)?.rules?.police_croquis_serie_number,
                pattern: {
                  value: /^[0-9\u06F0-\u06F9]+$/,
                  message: 'فقط عدد وارد کنید.',
                },
                minLength: {
                  value: 5,
                  message: 'حداقل پنج رقم وارد کنید.',
                },
                maxLength: {
                  value: 7,
                  message: 'حداکثر هفت رقم وارد کنید.',
                },
              }}
              disabled={
                fieldRules(reportType)?.disabled?.police_croquis_serie_number
              }
              optionalLabel={
                !fieldRules(reportType)?.rules?.police_croquis_serie_number
              }
            />
            <Divider color="gray-1" />
            <Flex
              border="1px solid #c2c2c2"
              borderRadius="8px"
              p="8px"
              justifyContent="center"
              alignItems="center"
            >
              <Typography color="GrayText" textStyle="b2">
                استعلام بر اساس کد کارشناس و نام افسر{' '}
              </Typography>
              <Button
                mr="auto"
                width={80}
                variant="text"
                color="primary"
                size="small"
                height={40}
                fontSize={14}
                onClick={openModal}
                type="button"
              >
                استعلام
                <ArrowRightIcon
                  className={css({
                    position: 'relative',
                    top: '2px',
                    height: '8px',
                    width: '8px',
                    strokeWidth: '20px',
                  })}
                  stroke={token('colors.purple-2')}
                />
              </Button>
            </Flex>

            <TextInput
              name="police_report_code"
              label="کد کارشناسی"
              placeholder="کد کارشناسی را وارد کنید"
              rules={{
                required: fieldRules(reportType)?.rules?.police_report_code,
                maxLength: {
                  value: 20,
                  message: 'حداکثر 20 رقم وارد کنید.',
                },
              }}
              optionalLabel={!fieldRules(reportType)?.rules?.police_report_code}
            />
            <TextInput
              name="police_name"
              label="نام افسر"
              placeholder="نام افسر را وارد کنید"
              rules={{
                required: fieldRules(reportType)?.rules?.police_name,
              }}
              optionalLabel={!fieldRules(reportType)?.rules?.police_name}
            />

            <Divider color="gray-1" />

            <TextInput
              name="police_report_body"
              label="شرح گزارش"
              placeholder="توضیحات را وارد نمایید"
              rules={{
                required: fieldRules(reportType)?.rules?.police_report_body,
                maxLength: {
                  value: 2000,
                  message: 'حداکثر ۲۰۰۰ کاراکتر وارد کنید.',
                },
              }}
              optionalLabel={!fieldRules(reportType)?.rules?.police_report_body}
            />
          </Flex>
          <LoadingButton
            loading={postLoading || policeReportLoading}
            fullWidth
            type="submit"
            disabled={!methods.formState.isValid}
          >
            ثبت
          </LoadingButton>
        </form>
        <PoliceReportInquiryModal
          fieldRules={fieldRules}
          open={open}
          closeModal={closeModal}
          submitInquiry={(data) => {
            methods.setValue('police_report_code', data?.value, {
              shouldValidate: true,
              shouldDirty: true,
              shouldTouch: true,
            });
            methods.setValue('police_name', data.label, {
              shouldValidate: true,
              shouldDirty: true,
              shouldTouch: true,
            });
          }}
        />
      </FormProvider>
    </Box>
  );
};
